import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction'; // needed for dateClick
import { ru } from 'date-fns/locale/ru';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { MagnifyingGlassIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import Select from 'react-select'; // Import react-select
// Import DatePicker and styles
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Register Russian locale for DatePicker
registerLocale('ru', ru);

interface StructureResource {
    id: string; // FullCalendar uses string IDs for resources
    title: string; // e.g., "BLB-001 - Биллборд"
    // Add other structure details if needed for rendering
    type?: string;
}

// Updated interface assuming backend provides order details
interface OccupancyEventInfo {
    structureId: number;
    date: string;
    order: {
        id: number;
        orderNumber: string;
        clientName: string;
        startDate: string;
        endDate: string;
    } | null;
}

const StructureOccupancyCalendar: React.FC = () => {
    const calendarRef = useRef<FullCalendar>(null);
    const [currentDate, setCurrentDate] = useState(new Date()); // This now controls the selected month
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [allResources, setAllResources] = useState<StructureResource[]>([]); // Store all fetched resources
    const [events, setEvents] = useState<any[]>([]); // Events for the calendar
    const [searchTerm, setSearchTerm] = useState(''); // State for search term
    const [structureTypes, setStructureTypes] = useState<string[]>([]); // State for types
    const [selectedType, setSelectedType] = useState<string>('all'); // State for selected type
    const [loadingTypes, setLoadingTypes] = useState(true); // Loading state for types

    const fetchCalendarData = useCallback(async (date: Date) => {
        setLoading(true);
        setError(null);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;

        try {
            // Fetch all active structures to use as resources
            const structuresResponse = await axios.get<{ id: number, code: string, type: string }[]>('/structures/active');
            const structureResources: StructureResource[] = structuresResponse.data.map(s => ({
                id: String(s.id), // Convert ID to string for FullCalendar
                title: `${s.code} (${s.type})`,
                type: s.type,
            }));
            setAllResources(structureResources);

            // Fetch detailed occupancy data for the current month
            // Assumes endpoint now returns OccupancyEventInfo[]
            const occupancyResponse = await axios.get<OccupancyEventInfo[]>('/structures/occupancy', {
                params: { year, month, details: true } // Add details=true param if backend needs it
            });

            // Transform occupancy data into FullCalendar events
            const calendarEvents = occupancyResponse.data.map(info => ({
                resourceId: String(info.structureId),
                start: info.date,
                end: info.date,
                allDay: true,
                // Pass order details to extendedProps for use in renderEventContent
                extendedProps: {
                    order: info.order
                }
                // No display: 'background' or color needed here anymore
            }));
            setEvents(calendarEvents);

        } catch (err) {
            console.error("Error fetching calendar data:", err);
            setError("Ошибка загрузки данных календаря занятости.");
            setAllResources([]);
            setEvents([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch structure types
    useEffect(() => {
        setLoadingTypes(true);
        axios.get<string[]>('/settings/structure-types')
            .then(response => {
                setStructureTypes(response.data || []);
            })
            .catch(err => {
                console.error('Error fetching structure types:', err);
                setStructureTypes([]);
            })
            .finally(() => {
                setLoadingTypes(false);
            });
    }, []);

    // Prepare options for react-select type filter
    const typeOptions = useMemo(() => [
        { value: 'all', label: 'Все типы' },
        ...structureTypes.map(type => ({ value: type, label: type }))
    ], [structureTypes]);

    // Filter resources based on search term AND selected type
    const filteredResources = useMemo(() => {
        return allResources.filter(resource => {
            const lowerSearchTerm = searchTerm.toLowerCase();
            const typeMatch = selectedType === 'all' || resource.type === selectedType;
            const searchMatch = !searchTerm || resource.title.toLowerCase().includes(lowerSearchTerm);
            return typeMatch && searchMatch;
        });
    }, [allResources, searchTerm, selectedType]);

    useEffect(() => {
        fetchCalendarData(currentDate);
    }, [fetchCalendarData, currentDate]);

    const handleToday = () => {
        const today = new Date();
        setCurrentDate(today); // Update current date state
    }

    // Handler for DatePicker change
    const handleMonthChange = (date: Date | null) => {
        if (date) {
            setCurrentDate(date); // Update current date state
        }
    };

    // Function to handle click on an occupied cell
    const handleOccupancyClick = (orderId: number | undefined) => {
        if (orderId) {
            window.open(`/orders/${orderId}`, '_blank', 'noopener,noreferrer'); // Open order in new tab
        }
    };

    // Custom function to render event content
    const renderEventContent = (eventInfo: any) => {
        const order = eventInfo.event.extendedProps.order;
        let tooltipText = 'Занято';
        let cursorClass = 'cursor-default';
        let onClickHandler: (() => void) | undefined = undefined;

        if (order) {
            tooltipText = `Заказ: ${order.orderNumber}\nКлиент: ${order.clientName}\nПериод: ${order.startDate} - ${order.endDate}`;
            cursorClass = 'cursor-pointer';
            onClickHandler = () => handleOccupancyClick(order.id);
        }

        return (
            <div
                className={`flex items-center justify-center h-full w-full ${cursorClass}`}
                title={tooltipText}
                onClick={onClickHandler} // Add onClick handler
                role={order ? 'button' : undefined} // Accessibility hint
                tabIndex={order ? 0 : undefined} // Make it focusable if clickable
                onKeyDown={order ? (e) => { if (e.key === 'Enter' || e.key === ' ') { handleOccupancyClick(order.id); } } : undefined} // Keyboard accessibility
            >
                <LockClosedIcon className="h-4 w-4 text-red-600" />
            </div>
        );
    };

    return (
        <div>
            <PageHeader
                title="Календарь занятости конструкций"
                subtitle="Визуализация занятости по дням"
                backLink="/structures"
                actions={
                    <div className="flex space-x-2">
                        {/* Remove Prev/Next Buttons */}
                        {/* <button onClick={handlePrevMonth} className="btn-secondary-outline">{'<'}</button> */}
                        <button onClick={handleToday} className="btn-secondary-outline">Сегодня</button>
                        {/* <button onClick={handleNextMonth} className="btn-secondary-outline">{ '>'}</button> */}
                    </div>
                }
            />

            {/* Filters Row */}
            <div className="mb-4 bg-white p-3 rounded-lg shadow flex flex-wrap items-center gap-4">
                {/* Month Picker Wrapper */}
                <div className="flex-grow sm:flex-grow-0 relative z-[1000]">
                    <label htmlFor="month-picker" className="sr-only">Месяц</label>
                    <DatePicker
                        selected={currentDate}
                        onChange={handleMonthChange}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        locale={ru}
                        className="form-input w-full sm:w-auto"
                        wrapperClassName="w-full sm:w-auto"
                        id="month-picker"
                        popperPlacement="bottom-start"
                        popperClassName="z-[1000]" // Keep this high too, just in case
                    />
                </div>
                {/* Search Input */}
                <div className="flex-grow min-w-[200px]">
                    <label htmlFor="calendar-search" className="sr-only">Поиск</label>
                    <div className="relative">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                        <input
                            id="calendar-search"
                            type="text"
                            placeholder="Поиск по коду/типу..."
                            className="form-input pl-10 w-full"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                {/* Type Filter Wrapper with relative position and z-index */}
                <div className="flex-grow min-w-[180px] relative z-[1000]">
                    <label htmlFor="calendar-type-filter" className="sr-only">Тип конструкции</label>
                    <Select
                        id="calendar-type-filter"
                        instanceId="calendar-type-filter-select"
                        options={typeOptions}
                        value={typeOptions.find(opt => opt.value === selectedType)}
                        onChange={(option: any) => setSelectedType(option?.value || 'all')}
                        isLoading={loadingTypes}
                        placeholder="Фильтр по типу..."
                        classNamePrefix="react-select"
                    // Optionally control menu position if needed
                    // menuPortalTarget={document.body} 
                    // styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow p-4">
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[resourceTimelinePlugin, interactionPlugin]}
                        initialView="resourceTimelineMonth"
                        schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
                        locale={ru}
                        headerToolbar={false} // Use custom header buttons
                        resourceAreaHeaderContent="Конструкции"
                        resources={filteredResources}
                        events={events}
                        editable={false} // Prevent dragging/resizing
                        droppable={false}
                        height="auto" // Adjust height as needed
                        resourceAreaWidth="25%" // Adjust width for resource names
                        slotMinWidth={40} // Adjust min width for day cells
                        eventContent={renderEventContent}
                        initialDate={currentDate} // Set initial date to currentDate
                    />
                )}
                {/* Update messages based on filters */}
                {!loading && filteredResources.length === 0 && allResources.length > 0 && (
                    <p className="text-center text-gray-500 mt-4">
                        Конструкции, соответствующие фильтрам, не найдены.
                    </p>
                )}
                {!loading && allResources.length === 0 && !error && (
                    <p className="text-center text-gray-500 mt-4">
                        Нет активных конструкций для отображения.
                    </p>
                )}
            </div>
        </div>
    );
};

export default StructureOccupancyCalendar;
